import ROUTES from '../constants/routes';
import { isStringEmpty, isStringNotEmpty } from './objectUtils';
import { dumpHistoryMutationLog, setHistoryMutationObserver } from './historyMutationLog';

// shared parser so bootstrap, analytics, and funnel init agree on what counts as a funnel
// route; variant is intentionally excluded - the funnel pre-selects the default at checkout
export const normalizeFunnelRoute = (pathname, search = '') => {
	const result = {
		isFunnelRoute: false,
		isCheckoutRoute: false,
		programSlug: null,
		productSku: null,
		version: null
	};

	if (isStringEmpty(pathname)) {
		return result;
	}

	const segments = pathname.split('/').filter((segment) => isStringNotEmpty(segment));
	if (segments.length < 1 || segments.length > 3) {
		return result;
	}

	const prefix = '/' + segments[0];
	if (prefix !== ROUTES.PROGRAM_FUNNEL && prefix !== ROUTES.PROGRAM_FUNNEL_CHECKOUT) {
		return result;
	}

	const params = new URLSearchParams(search ?? '');

	result.isFunnelRoute = true;
	result.isCheckoutRoute = prefix === ROUTES.PROGRAM_FUNNEL_CHECKOUT;
	result.programSlug = segments[1] ?? null;
	result.productSku = segments[2] ?? null;
	result.version = params.get('v') ?? null;

	return result;
};

// the routes that render from a program bundle rather than the full orchestrator: the sales funnel,
// checkout, and the encounter funnel the customer lands on straight after paying. Full hydration is
// deferred until they leave, so the ~10MB catalog load never competes with any of them.
export const isBundleScopedRoute = (pathname) => {
	if (isStringEmpty(pathname)) {
		return false;
	}

	const segments = pathname.split('/').filter((segment) => isStringNotEmpty(segment));
	if (segments.length < 1) {
		return false;
	}

	const prefix = '/' + segments[0];

	return prefix === ROUTES.PROGRAM_FUNNEL || prefix === ROUTES.PROGRAM_FUNNEL_CHECKOUT || prefix === ROUTES.ENCOUNTER;
};

// entries record whole URLs; the route checks take a pathname. a relative URL still parses when it is
// given a base, and anything unparseable answers empty rather than throwing inside an observer
const toPathname = (value) => {
	if (isStringEmpty(value)) {
		return '';
	}

	try {
		return new URL(value, window.location.origin).pathname;
	} catch (error) {
		return '';
	}
};

// Leaving /f, /fc or /e mid-journey is the shape of every navigation defect seen on this path so far -
// the post-payment redirect that reverted, and a session that left the funnel for "/" one click after
// the gender step. Both were reconstructed hours later from stored records because nothing recorded who
// navigated. This dumps the history log, with the stack that made the mutation, on any departure.
//
// It deliberately does not judge the destination. "/" is ROUTES.LANDING and goToAccount navigates there
// on purpose, so a destination allowlist would have suppressed exactly the case worth seeing. The stack
// is the evidence: it says whether goToAccount, leaveEvaluation or something unexpected made the call.
export const installFunnelRouteDepartureWatch = () => {
	setHistoryMutationObserver((entry) => {
		const from = toPathname(entry?.from);
		const to = toPathname(entry?.to);

		// `from === to` is an early-out, NOT a guard: if the two paths are equal the bundle-scoped test
		// below cannot fire either, so removing it changes no outcome and no test can pin it on its own
		if (isStringEmpty(from) || isStringEmpty(to) || from === to) {
			return;
		}

		if (isBundleScopedRoute(from) && isBundleScopedRoute(to) === false) {
			dumpHistoryMutationLog(`left the funnel journey: ${from} -> ${to}`);
		}
	});
};

// fast boot skips awaiting the full orchestrator on funnel routes. Disable at build time with
// REACT_APP_FUNNEL_FAST_BOOT=false, or per-request with ?fastboot=false for debugging.
export const isFunnelFastBootEnabled = (search = '') => {
	const params = new URLSearchParams(search ?? '');
	const override = params.get('fastboot');
	if (isStringNotEmpty(override)) {
		return override !== '0' && override.toLowerCase() !== 'false';
	}

	return process.env.REACT_APP_FUNNEL_FAST_BOOT !== 'false';
};

// a queued router transition from a funnel effect can still commit after the post-payment redirect
// and pull the customer back off the encounter. A paid customer landing anywhere else is the worst
// failure the funnel has, so this verifies the destination and forces a hard navigation if the
// router lost it - deliberately independent of refs and step state so every payment path is covered.
const ENCOUNTER_REDIRECT_VERIFY_MS = 500;

export const verifyEncounterRedirect = (encounterId, search = '') => {
	if (isStringEmpty(encounterId)) {
		return;
	}

	const encounterPath = ROUTES.ENCOUNTER + `/${encounterId}`;
	setTimeout(() => {
		if (!window.location.pathname.startsWith(ROUTES.ENCOUNTER + '/')) {
			console.log('redirect: navigation was reverted, forcing hard navigation to', encounterPath);
			dumpHistoryMutationLog('encounter redirect reverted');
			window.location.assign(`${encounterPath}${isStringNotEmpty(search) ? `?${search}` : ''}`);
		}
	}, ENCOUNTER_REDIRECT_VERIFY_MS);
};

// runs work after the next browser paint so it never competes with first render.
// requestAnimationFrame never fires in hidden tabs (e.g. a funnel opened via middle-click), so
// hidden documents schedule via setTimeout instead - hydration and listeners must not wait for
// the tab to gain focus.
export const runAfterFirstPaint = (fn) => {
	if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
		setTimeout(fn, 0);
	} else if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
		window.requestAnimationFrame(() => { setTimeout(fn, 0); });
	} else {
		setTimeout(fn, 0);
	}
};
